import React, { useState, useEffect } from 'react';
import { ArrowLeft, Shield, Lock, Eye, Database, Trash2, Globe, ChevronDown, Bot, UserCheck } from 'lucide-react';
import EgovexLogo from '../components/EgovexLogo';
import ContactForm from '../components/ContactForm';
import { trackPageView } from '../src/lib/analytics';

interface PrivacyViewProps {
  onBack: () => void;
  lang: 'en' | 'it' | 'es' | 'de' | 'fr';
  onLangChange?: (lang: 'en' | 'it' | 'es' | 'de' | 'fr') => void;
  onHome?: () => void;
}

const PRIVACY_CONTENT: Record<string, {
  title: string;
  lastUpdated: string;
  intro: string;
  sections: { title: string; icon: any; content: string[] }[];
}> = {
  en: {
    title: 'Privacy Policy',
    lastUpdated: 'Last updated: December 2025 | Version 2025.12.1',
    intro: 'At Egovex, your privacy is fundamental. This policy explains how we collect, use, and protect your personal data in compliance with GDPR and the EU AI Act.',
    sections: [
      {
        title: 'Data We Collect',
        icon: Database,
        content: [
          'Account information: name, email, language preference',
          'Content you create: journal entries, decisions, values, goals',
          'Usage patterns: how you interact with the app (anonymized)',
          'Device information: browser type, operating system (for optimization)',
          'Consent records: timestamps and audit trail of your privacy choices'
        ]
      },
      {
        title: 'How We Use Your Data',
        icon: Eye,
        content: [
          'Provide personalized AI insights based on your entries',
          'Recognize patterns in your life to help you grow',
          'Improve our AI models (using anonymized, aggregated data only)',
          'Send important service updates (never marketing without consent)'
        ]
      },
      {
        title: 'AI Transparency (EU AI Act)',
        icon: Bot,
        content: [
          'Egovex is classified as a low-risk AI personal assistant under EU AI Act',
          'AI analyzes your text to identify emotions, patterns, and provide suggestions',
          'AI decisions are advisory only - you always maintain full control',
          'We use third-party AI models (OpenAI) with data processing agreements',
          'Your data is not used to train external AI models without consent'
        ]
      },
      {
        title: 'Human Oversight',
        icon: UserCheck,
        content: [
          'All AI-generated insights are clearly labeled as suggestions',
          'You can disable AI features at any time in settings',
          'Critical decisions always recommend consulting professionals',
          'Our team monitors AI outputs for quality and safety'
        ]
      },
      {
        title: 'Data Security',
        icon: Lock,
        content: [
          'End-to-end encryption for all personal content',
          'Data stored on secure, SOC 2 compliant servers in the EU',
          'Regular security audits and penetration testing',
          'Your data is never sold to third parties'
        ]
      },
      {
        title: 'Your Rights (GDPR)',
        icon: Shield,
        content: [
          'Access: Request a copy of all your data anytime',
          'Correction: Update or correct your information',
          'Deletion: Permanently delete your account and all data',
          'Portability: Export your data in standard formats',
          'Objection: Object to specific data processing activities',
          'Explanation: Request explanation of AI-driven decisions'
        ]
      },
      {
        title: 'Data Deletion',
        icon: Trash2,
        content: [
          'You can delete individual entries or your entire account',
          'Deletion is permanent and irreversible',
          'We retain anonymized analytics data for service improvement',
          'Account deletion removes all personal data within 30 days'
        ]
      }
    ]
  },
  it: {
    title: 'Informativa sulla Privacy',
    lastUpdated: 'Ultimo aggiornamento: Dicembre 2025 | Versione 2025.12.1',
    intro: 'In Egovex, la tua privacy è fondamentale. Questa policy spiega come raccogliamo, utilizziamo e proteggiamo i tuoi dati personali in conformità con GDPR e AI Act UE.',
    sections: [
      {
        title: 'Dati che Raccogliamo',
        icon: Database,
        content: [
          'Informazioni account: nome, email, preferenza lingua',
          'Contenuti che crei: momenti, decisioni, valori, obiettivi',
          'Pattern di utilizzo: come interagisci con l\'app (anonimizzati)',
          'Informazioni dispositivo: tipo browser, sistema operativo',
          'Registri consenso: timestamp e traccia delle tue scelte privacy'
        ]
      },
      {
        title: 'Come Utilizziamo i Tuoi Dati',
        icon: Eye,
        content: [
          'Fornire insight AI personalizzati basati sui tuoi momenti',
          'Riconoscere pattern nella tua vita per aiutarti a crescere',
          'Migliorare i nostri modelli AI (solo dati anonimizzati e aggregati)',
          'Inviare aggiornamenti importanti (mai marketing senza consenso)'
        ]
      },
      {
        title: 'Trasparenza AI (AI Act UE)',
        icon: Bot,
        content: [
          'Egovex è classificato come assistente personale AI a basso rischio secondo l\'AI Act UE',
          'L\'AI analizza il tuo testo per identificare emozioni, pattern e fornire suggerimenti',
          'Le decisioni AI sono solo consultive - mantieni sempre il pieno controllo',
          'Utilizziamo modelli AI di terze parti (OpenAI) con accordi di elaborazione dati',
          'I tuoi dati non vengono usati per addestrare modelli AI esterni senza consenso'
        ]
      },
      {
        title: 'Supervisione Umana',
        icon: UserCheck,
        content: [
          'Tutti gli insight generati dall\'AI sono chiaramente etichettati come suggerimenti',
          'Puoi disabilitare le funzionalità AI in qualsiasi momento nelle impostazioni',
          'Le decisioni critiche raccomandano sempre di consultare professionisti',
          'Il nostro team monitora gli output AI per qualità e sicurezza'
        ]
      },
      {
        title: 'Sicurezza dei Dati',
        icon: Lock,
        content: [
          'Crittografia end-to-end per tutti i contenuti personali',
          'Dati archiviati su server sicuri e conformi SOC 2 nell\'UE',
          'Audit di sicurezza regolari e test di penetrazione',
          'I tuoi dati non vengono mai venduti a terzi'
        ]
      },
      {
        title: 'I Tuoi Diritti (GDPR)',
        icon: Shield,
        content: [
          'Accesso: Richiedi una copia di tutti i tuoi dati',
          'Rettifica: Aggiorna o correggi le tue informazioni',
          'Cancellazione: Elimina permanentemente account e dati',
          'Portabilità: Esporta i tuoi dati in formati standard',
          'Opposizione: Opponiti a specifiche attività di elaborazione',
          'Spiegazione: Richiedi spiegazione delle decisioni guidate dall\'AI'
        ]
      },
      {
        title: 'Cancellazione Dati',
        icon: Trash2,
        content: [
          'Puoi eliminare singoli momenti o l\'intero account',
          'La cancellazione è permanente e irreversibile',
          'Manteniamo dati analytics anonimizzati per il miglioramento',
          'La cancellazione account rimuove tutti i dati entro 30 giorni'
        ]
      }
    ]
  },
  es: {
    title: 'Política de Privacidad',
    lastUpdated: 'Última actualización: Diciembre 2025 | Versión 2025.12.1',
    intro: 'En Egovex, tu privacidad es fundamental. Esta política explica cómo recopilamos, usamos y protegemos tus datos personales en cumplimiento con GDPR y la Ley de IA de la UE.',
    sections: [
      {
        title: 'Datos que Recopilamos',
        icon: Database,
        content: [
          'Información de cuenta: nombre, email, preferencia de idioma',
          'Contenido que creas: momentos, decisiones, valores, metas',
          'Patrones de uso: cómo interactúas con la app (anonimizados)',
          'Información del dispositivo: tipo de navegador, sistema operativo',
          'Registros de consentimiento: timestamps y auditoría de tus elecciones de privacidad'
        ]
      },
      {
        title: 'Cómo Usamos tus Datos',
        icon: Eye,
        content: [
          'Proporcionar insights AI personalizados basados en tus momentos',
          'Reconocer patrones en tu vida para ayudarte a crecer',
          'Mejorar nuestros modelos AI (solo datos anonimizados)',
          'Enviar actualizaciones importantes (nunca marketing sin consentimiento)'
        ]
      },
      {
        title: 'Transparencia de IA (Ley de IA UE)',
        icon: Bot,
        content: [
          'Egovex está clasificado como asistente personal de IA de bajo riesgo según la Ley de IA de la UE',
          'La IA analiza tu texto para identificar emociones, patrones y proporcionar sugerencias',
          'Las decisiones de IA son solo orientativas - siempre mantienes el control total',
          'Usamos modelos de IA de terceros (OpenAI) con acuerdos de procesamiento de datos',
          'Tus datos no se usan para entrenar modelos de IA externos sin consentimiento'
        ]
      },
      {
        title: 'Supervisión Humana',
        icon: UserCheck,
        content: [
          'Todos los insights generados por IA están claramente etiquetados como sugerencias',
          'Puedes desactivar las funciones de IA en cualquier momento en configuración',
          'Las decisiones críticas siempre recomiendan consultar a profesionales',
          'Nuestro equipo monitorea las salidas de IA para calidad y seguridad'
        ]
      },
      {
        title: 'Seguridad de Datos',
        icon: Lock,
        content: [
          'Cifrado de extremo a extremo para todo el contenido personal',
          'Datos almacenados en servidores seguros SOC 2 en la UE',
          'Auditorías de seguridad regulares y pruebas de penetración',
          'Tus datos nunca se venden a terceros'
        ]
      },
      {
        title: 'Tus Derechos (GDPR)',
        icon: Shield,
        content: [
          'Acceso: Solicita una copia de todos tus datos',
          'Rectificación: Actualiza o corrige tu información',
          'Eliminación: Borra permanentemente tu cuenta y datos',
          'Portabilidad: Exporta tus datos en formatos estándar',
          'Oposición: Objeta actividades específicas de procesamiento',
          'Explicación: Solicita explicación de decisiones impulsadas por IA'
        ]
      },
      {
        title: 'Eliminación de Datos',
        icon: Trash2,
        content: [
          'Puedes eliminar momentos individuales o toda tu cuenta',
          'La eliminación es permanente e irreversible',
          'Mantenemos datos analíticos anonimizados',
          'La eliminación de cuenta remueve todos los datos en 30 días'
        ]
      }
    ]
  },
  de: {
    title: 'Datenschutzrichtlinie',
    lastUpdated: 'Zuletzt aktualisiert: Dezember 2025 | Version 2025.12.1',
    intro: 'Bei Egovex ist Ihre Privatsphäre grundlegend. Diese Richtlinie erklärt, wie wir Ihre persönlichen Daten in Übereinstimmung mit DSGVO und EU-KI-Gesetz sammeln, nutzen und schützen.',
    sections: [
      {
        title: 'Daten, die wir sammeln',
        icon: Database,
        content: [
          'Kontoinformationen: Name, E-Mail, Sprachpräferenz',
          'Von Ihnen erstellte Inhalte: Momente, Entscheidungen, Werte, Ziele',
          'Nutzungsmuster: Wie Sie mit der App interagieren (anonymisiert)',
          'Geräteinformationen: Browsertyp, Betriebssystem',
          'Einwilligungsaufzeichnungen: Zeitstempel und Prüfpfad Ihrer Datenschutzentscheidungen'
        ]
      },
      {
        title: 'Wie wir Ihre Daten nutzen',
        icon: Eye,
        content: [
          'Personalisierte KI-Einblicke basierend auf Ihren Momenten',
          'Muster in Ihrem Leben erkennen, um Ihr Wachstum zu fördern',
          'Unsere KI-Modelle verbessern (nur anonymisierte Daten)',
          'Wichtige Service-Updates senden (niemals Marketing ohne Einwilligung)'
        ]
      },
      {
        title: 'KI-Transparenz (EU-KI-Gesetz)',
        icon: Bot,
        content: [
          'Egovex ist als risikoarmer KI-Persönlicher-Assistent gemäß EU-KI-Gesetz eingestuft',
          'KI analysiert Ihren Text, um Emotionen, Muster zu erkennen und Vorschläge zu machen',
          'KI-Entscheidungen sind nur beratend - Sie behalten immer die volle Kontrolle',
          'Wir nutzen KI-Modelle von Drittanbietern (OpenAI) mit Datenverarbeitungsvereinbarungen',
          'Ihre Daten werden nicht ohne Einwilligung zum Training externer KI-Modelle verwendet'
        ]
      },
      {
        title: 'Menschliche Aufsicht',
        icon: UserCheck,
        content: [
          'Alle KI-generierten Einblicke sind klar als Vorschläge gekennzeichnet',
          'Sie können KI-Funktionen jederzeit in den Einstellungen deaktivieren',
          'Kritische Entscheidungen empfehlen immer die Konsultation von Fachleuten',
          'Unser Team überwacht KI-Ausgaben auf Qualität und Sicherheit'
        ]
      },
      {
        title: 'Datensicherheit',
        icon: Lock,
        content: [
          'Ende-zu-Ende-Verschlüsselung für alle persönlichen Inhalte',
          'Daten auf sicheren, SOC 2-konformen Servern in der EU gespeichert',
          'Regelmäßige Sicherheitsaudits und Penetrationstests',
          'Ihre Daten werden niemals an Dritte verkauft'
        ]
      },
      {
        title: 'Ihre Rechte (DSGVO)',
        icon: Shield,
        content: [
          'Zugang: Fordern Sie jederzeit eine Kopie aller Ihrer Daten an',
          'Berichtigung: Aktualisieren oder korrigieren Sie Ihre Informationen',
          'Löschung: Löschen Sie Ihr Konto und alle Daten dauerhaft',
          'Portabilität: Exportieren Sie Ihre Daten in Standardformaten',
          'Widerspruch: Widersprechen Sie bestimmten Verarbeitungsaktivitäten',
          'Erklärung: Fordern Sie Erklärung für KI-gesteuerte Entscheidungen an'
        ]
      },
      {
        title: 'Datenlöschung',
        icon: Trash2,
        content: [
          'Sie können einzelne Einträge oder Ihr gesamtes Konto löschen',
          'Die Löschung ist dauerhaft und irreversibel',
          'Wir behalten anonymisierte Analysedaten zur Verbesserung',
          'Kontolöschung entfernt alle persönlichen Daten innerhalb von 30 Tagen'
        ]
      }
    ]
  },
  fr: {
    title: 'Politique de Confidentialité',
    lastUpdated: 'Dernière mise à jour: Décembre 2025 | Version 2025.12.1',
    intro: 'Chez Egovex, votre vie privée est fondamentale. Cette politique explique comment nous collectons, utilisons et protégeons vos données personnelles conformément au RGPD et à la Loi IA de l\'UE.',
    sections: [
      {
        title: 'Données que nous collectons',
        icon: Database,
        content: [
          'Informations de compte: nom, email, préférence de langue',
          'Contenu que vous créez: moments, décisions, valeurs, objectifs',
          'Modèles d\'utilisation: comment vous interagissez avec l\'app (anonymisés)',
          'Informations appareil: type de navigateur, système d\'exploitation',
          'Enregistrements de consentement: horodatages et piste d\'audit de vos choix de confidentialité'
        ]
      },
      {
        title: 'Comment nous utilisons vos données',
        icon: Eye,
        content: [
          'Fournir des insights IA personnalisés basés sur vos moments',
          'Reconnaître les schémas dans votre vie pour vous aider à grandir',
          'Améliorer nos modèles IA (données anonymisées uniquement)',
          'Envoyer des mises à jour importantes (jamais de marketing sans consentement)'
        ]
      },
      {
        title: 'Transparence IA (Loi IA UE)',
        icon: Bot,
        content: [
          'Egovex est classé comme assistant personnel IA à faible risque selon la Loi IA de l\'UE',
          'L\'IA analyse votre texte pour identifier émotions, schémas et fournir des suggestions',
          'Les décisions IA sont uniquement consultatives - vous gardez toujours le contrôle total',
          'Nous utilisons des modèles IA tiers (OpenAI) avec des accords de traitement de données',
          'Vos données ne sont pas utilisées pour entraîner des modèles IA externes sans consentement'
        ]
      },
      {
        title: 'Supervision Humaine',
        icon: UserCheck,
        content: [
          'Tous les insights générés par l\'IA sont clairement étiquetés comme suggestions',
          'Vous pouvez désactiver les fonctionnalités IA à tout moment dans les paramètres',
          'Les décisions critiques recommandent toujours de consulter des professionnels',
          'Notre équipe surveille les sorties IA pour la qualité et la sécurité'
        ]
      },
      {
        title: 'Sécurité des données',
        icon: Lock,
        content: [
          'Chiffrement de bout en bout pour tout le contenu personnel',
          'Données stockées sur des serveurs sécurisés conformes SOC 2 dans l\'UE',
          'Audits de sécurité réguliers et tests de pénétration',
          'Vos données ne sont jamais vendues à des tiers'
        ]
      },
      {
        title: 'Vos droits (RGPD)',
        icon: Shield,
        content: [
          'Accès: Demandez une copie de toutes vos données',
          'Rectification: Mettez à jour ou corrigez vos informations',
          'Suppression: Supprimez définitivement votre compte et vos données',
          'Portabilité: Exportez vos données dans des formats standards',
          'Opposition: Objectez à des activités de traitement spécifiques',
          'Explication: Demandez une explication des décisions pilotées par l\'IA'
        ]
      },
      {
        title: 'Suppression des données',
        icon: Trash2,
        content: [
          'Vous pouvez supprimer des entrées individuelles ou tout votre compte',
          'La suppression est permanente et irréversible',
          'Nous conservons des données analytiques anonymisées',
          'La suppression de compte supprime toutes les données sous 30 jours'
        ]
      }
    ]
  }
};

const PrivacyView: React.FC<PrivacyViewProps> = ({ onBack, lang, onLangChange, onHome }) => {
  const content = PRIVACY_CONTENT[lang] || PRIVACY_CONTENT.en;
  const [showLangMenu, setShowLangMenu] = useState(false);

  useEffect(() => {
    trackPageView('/privacy', 'Privacy Policy', false);
  }, []);

  const languageLabels: Record<string, string> = {
    en: 'English',
    it: 'Italiano',
    es: 'Español',
    de: 'Deutsch',
    fr: 'Français'
  };

  return (
    <div className="min-h-screen bg-white">
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-black/[0.04]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={onHome || onBack}
              className="flex items-center gap-2 hover:opacity-80 transition-opacity"
            >
              <EgovexLogo size={32} />
              <span className="font-black text-lg tracking-tighter hidden sm:inline">Egovex</span>
            </button>
            <div className="h-6 w-px bg-black/10 hidden sm:block" />
            <button
              onClick={onBack}
              className="flex items-center gap-2 text-sm font-bold text-black/60 hover:text-black transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="hidden sm:inline">
                {lang === 'it' ? 'Indietro' : lang === 'es' ? 'Atrás' : lang === 'de' ? 'Zurück' : lang === 'fr' ? 'Retour' : 'Back'}
              </span>
            </button>
          </div>
          
          {onLangChange && (
            <div className="relative">
              <button
                onClick={() => setShowLangMenu(!showLangMenu)}
                className="flex items-center gap-2 px-3 py-1.5 rounded-full border border-black/10 hover:border-black/20 transition-colors text-sm font-medium"
              >
                <Globe className="w-4 h-4 text-black/40" />
                <span className="hidden sm:inline">{languageLabels[lang]}</span>
                <ChevronDown className={`w-3 h-3 text-black/40 transition-transform ${showLangMenu ? 'rotate-180' : ''}`} />
              </button>
              
              {showLangMenu && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setShowLangMenu(false)} />
                  <div className="absolute right-0 top-full mt-2 bg-white rounded-xl shadow-xl border border-black/10 py-2 z-50 min-w-[140px]">
                    {(['en', 'it', 'es', 'de', 'fr'] as const).map((l) => (
                      <button
                        key={l}
                        onClick={() => { onLangChange(l); setShowLangMenu(false); }}
                        className={`w-full px-4 py-2 text-left text-sm font-medium hover:bg-black/5 transition-colors ${lang === l ? 'text-indigo-600 bg-indigo-50' : 'text-black/70'}`}
                      >
                        {languageLabels[l]}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-12">
        <div className="mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-indigo-50 rounded-full mb-4">
            <Shield className="w-4 h-4 text-indigo-600" />
            <span className="text-[11px] font-black uppercase tracking-widest text-indigo-600">
              {lang === 'it' ? 'La tua privacy' : lang === 'es' ? 'Tu privacidad' : lang === 'de' ? 'Ihre Privatsphäre' : lang === 'fr' ? 'Votre vie privée' : 'Your Privacy'}
            </span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-black tracking-tighter text-[#1D1D1F] mb-3">
            {content.title}
          </h1>
          <p className="text-black/40 font-medium">{content.lastUpdated}</p>
        </div>

        <p className="text-lg text-black/60 leading-relaxed mb-12 font-medium">
          {content.intro}
        </p>

        <div className="space-y-12">
          {content.sections.map((section, idx) => (
            <div key={idx} className="bg-[#FAFAFA] rounded-3xl p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center">
                  <section.icon className="w-5 h-5 text-indigo-600" />
                </div>
                <h2 className="text-xl font-black tracking-tight text-[#1D1D1F]">
                  {section.title}
                </h2>
              </div>
              <ul className="space-y-3">
                {section.content.map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-black/60 font-medium">
                    <div className="w-1.5 h-1.5 rounded-full bg-indigo-400 mt-2 shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-16">
          <ContactForm lang={lang} />
        </div>
      </main>
    </div>
  );
};

export default PrivacyView;
